import React, { useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ReferenceArea,
} from 'recharts';
import {
  applyInfraFilters,
  calculateInfraAverageDurationHours,
  buildFilterLabel,
  distinctRowsBy,
  formatInfraDateTime,
  formatInfraDurationHours,
  formatInfraMonth,
  getInfraEventType,
  getInfraHistoryMonth,
  groupCount,
  isInfraClosedInPeriod,
  isInfraOpenedInPeriod,
  normalizeDisplayValue,
  normalizeNumber,
  sameFilter,
} from '../utils/infraAnalytics';
import { useInfraestruturaData } from '../context/InfraestruturaDataContext';
import InfraEvolutionNotice from './InfraEvolutionNotice';
import OsMetricsPanel from './OsMetricsPanel';

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const darkPanelClassName =
  'rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface)] p-4 shadow-[0_14px_34px_rgba(15,23,42,0.08)]';

const chartTooltipStyle = {
  background: 'var(--surface-strong)',
  border: '1px solid var(--border)',
  borderRadius: '12px',
  color: 'var(--text-primary)',
  boxShadow: '0 18px 40px rgba(15,23,42,0.12)',
};

const tableColumns = [
  { key: 'id_os', label: 'OS' },
  { key: 'assunto', label: 'Assunto' },
  { key: 'referencia', label: 'Referência' },
  { key: 'cidade', label: 'Cidade' },
  { key: 'status', label: 'Status' },
  { key: 'evento_periodo', label: 'Evento no período' },
  { key: 'data_abertura', label: 'Abertura' },
  { key: 'data_hora_execucao', label: 'Execução' },
  { key: 'data_fechamento', label: 'Fechamento' },
  { key: 'horas', label: 'Horas' },
  { key: 'valor', label: 'Valor' },
  { key: 'rompimento', label: 'Rompimento' },
];

const SummaryCard = ({ title, value, hint }) => (
  <div className={`${darkPanelClassName} flex h-full min-h-[148px] flex-col`}>
    <p className="max-w-full text-[10px] font-semibold uppercase tracking-[0.10em] text-[color:var(--text-muted)] sm:text-[11px]">{title}</p>
    <p className="mt-3 break-words text-[clamp(1.9rem,2.4vw,3rem)] font-semibold leading-[0.94] tracking-[-0.03em] text-[color:var(--text-primary)]">{value}</p>
    {hint ? <p className="mt-auto pt-3 text-xs leading-5 text-[color:var(--text-muted)]">{hint}</p> : null}
  </div>
);

const ClickableList = ({ items, onClick, activeField, filters }) => (
  <div className="space-y-2">
    {items.map((item) => {
      const isActive = Array.isArray(filters)
        && filters.some((filter) => sameFilter(filter, {
          field: activeField,
          value: item.value,
        }));

      return (
        <button
          key={`${activeField}:${String(item.value ?? 'null')}`}
          type="button"
          onClick={() => onClick(item)}
          className={`flex w-full items-center justify-between gap-3 rounded-[12px] border px-3 py-2 text-left text-sm transition-colors ${
            isActive
              ? 'border-[color:var(--accent)] bg-[color:var(--surface-muted)]/90 text-[color:var(--text-primary)]'
              : 'border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text-primary)] hover:border-[color:var(--header-control-border-subtle)] hover:bg-[color:var(--surface-muted)]/70'
          }`}
        >
          <span className="min-w-0 truncate">{item.label}</span>
          <span className="shrink-0 font-semibold text-[color:var(--accent)]">{item.valueFormatted}</span>
        </button>
      );
    })}
  </div>
);

const buildMonthlySeries = (rows) => {
  const map = new Map();

  distinctRowsBy(rows, 'id_os').forEach((row) => {
    const aberturaMonth = getInfraHistoryMonth(row, 'ano_mes_abertura');
    if (isInfraOpenedInPeriod(row) && aberturaMonth !== 'Não informado') {
      if (!map.has(aberturaMonth)) {
        map.set(aberturaMonth, { month: formatInfraMonth(aberturaMonth), rawMonth: aberturaMonth, finalizadas: 0, abertas: 0, valor: 0 });
      }
      map.get(aberturaMonth).abertas += 1;
    }

    const fechamentoMonth = getInfraHistoryMonth(row, 'ano_mes_fechamento');
    if (isInfraClosedInPeriod(row) && fechamentoMonth !== 'Não informado') {
      if (!map.has(fechamentoMonth)) {
        map.set(fechamentoMonth, { month: formatInfraMonth(fechamentoMonth), rawMonth: fechamentoMonth, finalizadas: 0, abertas: 0, valor: 0 });
      }

      const bucket = map.get(fechamentoMonth);
      bucket.finalizadas += 1;
      bucket.valor += normalizeNumber(row?.total_valor);
    }
  });

  return Array.from(map.values()).sort((a, b) => a.rawMonth.localeCompare(b.rawMonth, 'pt-BR'));
};

const OrdemServicoDashboard = ({ isMobile = false }) => {
  const {
    rowsRaw,
    rowsFiltered,
    evolutionMonths,
    filters,
    toggleFilter,
    selectHistoricalMonth,
    activeRangeMonth,
  } = useInfraestruturaData();
  const [tableSearch, setTableSearch] = useState('');
  const [subjectSearch, setSubjectSearch] = useState('');

  const uniqueRows = useMemo(() => distinctRowsBy(rowsFiltered, 'id_os'), [rowsFiltered]);
  const closedRows = useMemo(() => uniqueRows.filter((row) => isInfraClosedInPeriod(row)), [uniqueRows]);
  const openedRows = useMemo(() => uniqueRows.filter((row) => isInfraOpenedInPeriod(row)), [uniqueRows]);
  const rowsWithoutSubjectFilter = useMemo(
    () => applyInfraFilters(rowsRaw, filters.filter((filter) => filter.field !== 'assunto')),
    [filters, rowsRaw],
  );
  const rowsWithoutReferenceFilter = useMemo(
    () => applyInfraFilters(rowsRaw, filters.filter((filter) => filter.field !== 'estrutura_referencia_normalizada')),
    [filters, rowsRaw],
  );
  const rowsWithoutCityFilter = useMemo(
    () => applyInfraFilters(rowsRaw, filters.filter((filter) => filter.field !== 'cidade_nome')),
    [filters, rowsRaw],
  );
  const closedRowsWithoutSubjectFilter = useMemo(
    () => distinctRowsBy(rowsWithoutSubjectFilter, 'id_os').filter((row) => isInfraClosedInPeriod(row)),
    [rowsWithoutSubjectFilter],
  );
  const closedRowsWithoutReferenceFilter = useMemo(
    () => distinctRowsBy(rowsWithoutReferenceFilter, 'id_os').filter((row) => isInfraClosedInPeriod(row)),
    [rowsWithoutReferenceFilter],
  );
  const closedRowsWithoutCityFilter = useMemo(
    () => distinctRowsBy(rowsWithoutCityFilter, 'id_os').filter((row) => isInfraClosedInPeriod(row)),
    [rowsWithoutCityFilter],
  );
  const monthlySeries = useMemo(
    () => (Array.isArray(evolutionMonths) && evolutionMonths.length > 0
      ? evolutionMonths.map((item) => ({
        month: formatInfraMonth(item?.ano_mes || 'Não informado'),
        rawMonth: item?.ano_mes || 'Não informado',
        finalizadas: Number(item?.finalizadas ?? item?.total_os ?? 0),
        abertas: Number(item?.abertas ?? 0),
        total_os: Number(item?.total_os ?? 0),
        total_valor: Number(item?.total_valor ?? 0),
        rompimentos: Number(item?.rompimentos ?? 0),
        media_horas: Number(item?.media_horas ?? 0),
      }))
      : buildMonthlySeries(rowsFiltered)),
    [evolutionMonths, rowsFiltered],
  );

  const kpis = useMemo(() => {
    const totalAbertas = distinctRowsBy(openedRows, 'id_os').length;
    const totalFechadas = distinctRowsBy(closedRows, 'id_os').length;
    const mediaRompimentos = closedRows.length > 0
      ? (closedRows.reduce((acc, row) => acc + (normalizeNumber(row?.is_rompimento) ? 1 : 0), 0) / closedRows.length) * 100
      : 0;
    const mediaAberturaExecucao = calculateInfraAverageDurationHours(closedRows, 'data_abertura', 'data_hora_execucao');
    const mediaExecucaoFechamento = calculateInfraAverageDurationHours(closedRows, 'data_hora_execucao', 'data_fechamento');
    const mediaAberturaFechamento = calculateInfraAverageDurationHours(closedRows, 'data_abertura', 'data_fechamento');

    return {
      totalAbertas,
      totalFechadas,
      mediaRompimentos,
      mediaAberturaExecucao,
      mediaExecucaoFechamento,
      mediaAberturaFechamento,
    };
  }, [closedRows, openedRows]);

  const rankingAssunto = useMemo(
    () => groupCount(closedRowsWithoutSubjectFilter, 'assunto', { distinctBy: 'id_os' }),
    [closedRowsWithoutSubjectFilter],
  );
  const rankingReferencia = useMemo(
    () => groupCount(closedRowsWithoutReferenceFilter, 'estrutura_referencia_normalizada', { distinctBy: 'id_os' }).slice(0, 10),
    [closedRowsWithoutReferenceFilter],
  );
  const rankingCidade = useMemo(
    () => groupCount(closedRowsWithoutCityFilter, 'cidade_nome', { distinctBy: 'id_os' }).slice(0, 10),
    [closedRowsWithoutCityFilter],
  );
  const normalizedSubjectSearch = subjectSearch.trim().toLowerCase();
  const filteredRankingAssunto = useMemo(() => {
    if (!normalizedSubjectSearch) {
      return rankingAssunto;
    }

    return rankingAssunto.filter((item) => (
      String(item?.label ?? '').toLowerCase().includes(normalizedSubjectSearch)
    ));
  }, [normalizedSubjectSearch, rankingAssunto]);

  const tableRows = useMemo(
    () => rowsFiltered
      .map((row) => ({
        id_os: row?.id_os ?? '—',
        assunto: normalizeDisplayValue(row?.assunto),
        referencia: normalizeDisplayValue(row?.estrutura_referencia_normalizada || row?.estrutura_referencia),
        cidade: normalizeDisplayValue(row?.cidade_nome),
        status: String(row?.data_fechamento || '').trim() ? 'Finalizada' : 'Aberta',
        evento_periodo: getInfraEventType(row),
        data_abertura: formatInfraDateTime(row?.data_abertura),
        data_hora_execucao: formatInfraDateTime(row?.data_hora_execucao),
        data_fechamento: formatInfraDateTime(row?.data_fechamento),
        raw_data_fechamento: String(row?.data_fechamento || '').trim(),
        raw_data_abertura: String(row?.data_abertura || '').trim(),
        horas: normalizeNumber(row?.diferenca_fechamento_horas),
        valor: normalizeNumber(row?.total_valor),
        rompimento: normalizeNumber(row?.is_rompimento) ? 'Sim' : 'Não',
      }))
      .sort((a, b) => {
        const byClose = String(b.raw_data_fechamento).localeCompare(String(a.raw_data_fechamento), 'pt-BR');
        if (byClose !== 0) {
          return byClose;
        }

        return String(b.raw_data_abertura).localeCompare(String(a.raw_data_abertura), 'pt-BR');
      }),
    [rowsFiltered],
  );

  const normalizedTableSearch = tableSearch.trim().toLowerCase();

  const filteredTableRows = useMemo(() => {
    if (!normalizedTableSearch) {
      return tableRows;
    }

    return tableRows.filter((row) => [
      row.id_os,
      row.assunto,
      row.referencia,
      row.cidade,
      row.status,
      row.evento_periodo,
      row.data_abertura,
      row.data_hora_execucao,
      row.data_fechamento,
      formatInfraDurationHours(row.horas),
      currencyFormatter.format(row.valor),
      row.rompimento,
    ].some((value) => String(value ?? '').toLowerCase().includes(normalizedTableSearch)));
  }, [normalizedTableSearch, tableRows]);

  const handleFilter = (field, value, eventType = undefined) => {
    toggleFilter({
      field,
      value: value === 'Não informado' ? null : value,
      label: buildFilterLabel(field, value === 'Não informado' ? null : value),
      ...(eventType ? { eventType } : {}),
    });
  };

  const handleMonthSelect = (payload) => {
    const candidate = payload?.activePayload?.[0]?.payload
      || payload?.activePayload?.[1]?.payload
      || payload?.payload
      || payload;

    const rawMonth = candidate?.rawMonth || candidate?.month;

    if (rawMonth && rawMonth !== 'Não informado') {
      selectHistoricalMonth(rawMonth);
    }
  };

  const chartTickProps = isMobile ? { fontSize: 10 } : { fontSize: 11 };
  const chartMargin = isMobile
    ? { top: 8, right: 8, left: -14, bottom: 8 }
    : { top: 8, right: 20, left: 0, bottom: 8 };
  const xAxisProps = isMobile
    ? { interval: 'preserveStartEnd', minTickGap: 22, tickMargin: 8 }
    : { interval: 0, minTickGap: 10, tickMargin: 6 };
  const legendWrapperStyle = isMobile ? { paddingTop: 12, fontSize: 11 } : undefined;

  return (
    <div className="mt-4 w-full min-h-[720px] space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <SummaryCard title="Ordens abertas" value={kpis.totalAbertas.toLocaleString('pt-BR')} hint="Contagem distinta por id_os" />
        <SummaryCard title="Ordens finalizadas" value={kpis.totalFechadas.toLocaleString('pt-BR')} hint="Contagem distinta por id_os" />
        <SummaryCard title="Média de rompimentos" value={`${kpis.mediaRompimentos.toFixed(2)}%`} />
        <SummaryCard title="Abertura até execução" value={formatInfraDurationHours(kpis.mediaAberturaExecucao)} />
        <SummaryCard title="Execução até finalização" value={formatInfraDurationHours(kpis.mediaExecucaoFechamento)} />
        <SummaryCard title="Abertura até finalização" value={formatInfraDurationHours(kpis.mediaAberturaFechamento)} />
      </div>

      <OsMetricsPanel isMobile={isMobile} />

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-12">
        <div className={`${darkPanelClassName} xl:col-span-8`}>
          <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">Evolução mensal de OS</h3>
              {activeRangeMonth ? (
                <span className="rounded-full border border-[color:var(--accent)]/35 bg-[color:var(--accent)]/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-[color:var(--accent)]">
                  Mês ativo: {formatInfraMonth(activeRangeMonth)}
                </span>
              ) : null}
            </div>
            <span className="text-[11px] text-[color:var(--text-muted)]">Clique no mês para atualizar o período</span>
          </div>
          <InfraEvolutionNotice />
          <div className="h-[320px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlySeries} margin={chartMargin} onClick={handleMonthSelect} barCategoryGap={isMobile ? '24%' : '16%'}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.20)" />
                {activeRangeMonth ? (
                  <ReferenceArea
                    x1={formatInfraMonth(activeRangeMonth)}
                    x2={formatInfraMonth(activeRangeMonth)}
                    fill="rgba(59,130,246,0.10)"
                    strokeOpacity={0}
                  />
                ) : null}
                <XAxis dataKey="month" tick={chartTickProps} stroke="var(--text-muted)" {...xAxisProps} />
                <YAxis allowDecimals={false} tick={chartTickProps} width={isMobile ? 30 : 40} stroke="var(--text-muted)" />
                <Tooltip contentStyle={chartTooltipStyle} labelStyle={{ color: 'var(--text-primary)' }} itemStyle={{ color: 'var(--text-primary)' }} />
                <Legend wrapperStyle={legendWrapperStyle} iconSize={isMobile ? 10 : 14} />
                <Bar
                  dataKey="finalizadas"
                  name="Finalizadas"
                  fill="#2563eb"
                  radius={[6, 6, 0, 0]}
                  onClick={(payload) => {
                    const item = payload?.payload || payload;
                    if (item?.rawMonth || item?.month) {
                      const rawMonth = item?.rawMonth || item?.month;
                      if (rawMonth && rawMonth !== 'Não informado') {
                        selectHistoricalMonth(rawMonth);
                      }
                    }
                  }}
                >
                  {monthlySeries.map((item) => {
                    const isActive = item?.rawMonth === activeRangeMonth;
                    return (
                      <Cell
                        key={`finalizadas:${item?.rawMonth || item?.month}`}
                        fill={isActive ? '#60a5fa' : '#2563eb'}
                        stroke={isActive ? '#bfdbfe' : 'transparent'}
                        strokeWidth={isActive ? 2 : 0}
                      />
                    );
                  })}
                </Bar>
                <Bar
                  dataKey="abertas"
                  name="Abertas"
                  fill="#f59e0b"
                  radius={[6, 6, 0, 0]}
                  onClick={(payload) => {
                    const item = payload?.payload || payload;
                    if (item?.rawMonth || item?.month) {
                      const rawMonth = item?.rawMonth || item?.month;
                      if (rawMonth && rawMonth !== 'Não informado') {
                        selectHistoricalMonth(rawMonth);
                      }
                    }
                  }}
                >
                  {monthlySeries.map((item) => {
                    const isActive = item?.rawMonth === activeRangeMonth;
                    return (
                      <Cell
                        key={`abertas:${item?.rawMonth || item?.month}`}
                        fill={isActive ? '#fbbf24' : '#f59e0b'}
                        stroke={isActive ? '#fde68a' : 'transparent'}
                        strokeWidth={isActive ? 2 : 0}
                      />
                    );
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className={`${darkPanelClassName} xl:col-span-4`}>
          <h3 className="mb-3 text-sm font-semibold text-[color:var(--text-primary)]">Ordens por cidade</h3>
          <ClickableList
            items={rankingCidade.map((item) => ({
              label: item.label,
              value: item.label,
              valueFormatted: item.count.toLocaleString('pt-BR'),
            }))}
            onClick={(item) => handleFilter('cidade_nome', item.label)}
            activeField="cidade_nome"
            filters={filters}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
        <div className={darkPanelClassName}>
          <h3 className="mb-3 text-sm font-semibold text-[color:var(--text-primary)]">Ordens por assunto</h3>
          <div className="mb-3">
            <label className="sr-only" htmlFor="ordem-servico-subject-search">Pesquisar assuntos</label>
            <input
              id="ordem-servico-subject-search"
              type="search"
              value={subjectSearch}
              onChange={(event) => setSubjectSearch(event.target.value)}
              placeholder="Pesquisar assunto..."
              className="w-full rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-strong)] px-3 py-2 text-sm text-[color:var(--text-primary)] outline-none transition focus:border-[color:var(--accent)] focus:ring-2 focus:ring-[color:var(--accent)]/20"
            />
          </div>
          <div className="app-scroll-subtle max-h-[420px] overflow-y-auto pr-1">
            <ClickableList
              items={filteredRankingAssunto.map((item) => ({
                label: item.label,
                value: item.label,
                valueFormatted: item.count.toLocaleString('pt-BR'),
              }))}
              onClick={(item) => handleFilter('assunto', item.label)}
              activeField="assunto"
              filters={filters}
            />
          </div>
        </div>

        <div className={darkPanelClassName}>
          <h3 className="mb-3 text-sm font-semibold text-[color:var(--text-primary)]">Ordens por referência</h3>
          <ClickableList
            items={rankingReferencia.map((item) => ({
              label: item.label,
              value: item.label,
              valueFormatted: item.count.toLocaleString('pt-BR'),
            }))}
            onClick={(item) => handleFilter('estrutura_referencia_normalizada', item.label)}
            activeField="estrutura_referencia_normalizada"
            filters={filters}
          />
        </div>

        <div className={darkPanelClassName}>
          <h3 className="mb-3 text-sm font-semibold text-[color:var(--text-primary)]">Resumo por cidade</h3>
          <div className="text-xs text-[color:var(--text-muted)]">
            Clique em qualquer item acima para cruzar os dados entre todas as abas.
          </div>
          <div className="mt-3 overflow-hidden rounded-[12px] border border-[color:var(--border)] bg-[color:var(--surface)]">
            <table className="min-w-full text-sm">
              <thead className="bg-[color:var(--surface-muted)]/70 text-xs uppercase tracking-[0.08em] text-[color:var(--text-muted)]">
                <tr>
                  <th className="px-3 py-2 text-left">Cidade</th>
                  <th className="px-3 py-2 text-right">OS</th>
                </tr>
              </thead>
              <tbody>
                {rankingCidade.slice(0, 5).map((item) => (
                  <tr
                    key={item.label}
                    className="border-t border-[color:var(--border)]/70 transition-colors hover:bg-[color:var(--surface-muted)]/50"
                    onClick={() => handleFilter('cidade_nome', item.label)}
                  >
                    <td className="px-3 py-2 font-medium text-[color:var(--text-primary)]">{item.label}</td>
                    <td className="px-3 py-2 text-right">{item.count.toLocaleString('pt-BR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className={darkPanelClassName}>
        <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">Tabela detalhada</h3>
            <p className="mt-1 text-xs text-[color:var(--text-muted)]">
              {filteredTableRows.length.toLocaleString('pt-BR')} de {tableRows.length.toLocaleString('pt-BR')} registros visíveis
            </p>
          </div>
          <div className="w-full lg:max-w-sm">
            <label className="sr-only" htmlFor="ordem-servico-table-search">Pesquisar na tabela detalhada</label>
            <input
              id="ordem-servico-table-search"
              type="search"
              value={tableSearch}
              onChange={(event) => setTableSearch(event.target.value)}
              placeholder="Pesquisar por OS, assunto, referência, cidade..."
              className="w-full rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-strong)] px-3 py-2 text-sm text-[color:var(--text-primary)] outline-none transition focus:border-[color:var(--accent)] focus:ring-2 focus:ring-[color:var(--accent)]/20"
            />
          </div>
        </div>
        {isMobile ? (
          <div className="space-y-3">
            {filteredTableRows.map((row) => (
              <article
                key={`${row.id_os}-${row.data_fechamento}-${row.data_abertura}`}
                className="rounded-[18px] border border-[color:var(--border)] bg-[color:var(--surface-strong)] p-4 shadow-[0_10px_24px_rgba(15,23,42,0.08)]"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[color:var(--text-muted)]">
                      OS #{row.id_os}
                    </p>
                    <h4 className="mt-1 text-sm font-semibold leading-5 text-[color:var(--text-primary)]">
                      {row.assunto}
                    </h4>
                  </div>
                  <span className="rounded-full border border-[color:var(--header-control-border-subtle)] px-2.5 py-1 text-[11px] font-medium text-[color:var(--text-primary)]">
                    {row.status}
                  </span>
                </div>

                <div className="mt-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                  <div><span className="text-[color:var(--text-muted)]">Cidade:</span> <span className="text-[color:var(--text-primary)]">{row.cidade}</span></div>
                  <div><span className="text-[color:var(--text-muted)]">Referência:</span> <span className="text-[color:var(--text-primary)]">{row.referencia}</span></div>
                  <div><span className="text-[color:var(--text-muted)]">Evento no período:</span> <span className="text-[color:var(--text-primary)]">{row.evento_periodo}</span></div>
                  <div><span className="text-[color:var(--text-muted)]">Rompimento:</span> <span className="text-[color:var(--text-primary)]">{row.rompimento}</span></div>
                  <div><span className="text-[color:var(--text-muted)]">Abertura:</span> <span className="text-[color:var(--text-primary)]">{row.data_abertura}</span></div>
                  <div><span className="text-[color:var(--text-muted)]">Fechamento:</span> <span className="text-[color:var(--text-primary)]">{row.data_fechamento}</span></div>
                  <div><span className="text-[color:var(--text-muted)]">Execução:</span> <span className="text-[color:var(--text-primary)]">{row.data_hora_execucao}</span></div>
                  <div><span className="text-[color:var(--text-muted)]">Horas:</span> <span className="text-[color:var(--text-primary)]">{formatInfraDurationHours(row.horas)}</span></div>
                </div>

                <div className="mt-3 flex items-center justify-between rounded-[14px] border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface)] px-3 py-2">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[color:var(--text-muted)]">Valor</span>
                  <span className="text-sm font-semibold text-[color:var(--text-primary)]">{currencyFormatter.format(row.valor)}</span>
                </div>
              </article>
            ))}
            {filteredTableRows.length === 0 ? (
              <div className="rounded-[18px] border border-dashed border-[color:var(--border)] px-4 py-8 text-center text-sm text-[color:var(--text-muted)]">
                Nenhum registro encontrado para a busca informada.
              </div>
            ) : null}
          </div>
        ) : (
          <div className="max-h-[520px] overflow-auto rounded-xl border border-[color:var(--border)]/60">
            <table className="min-w-full text-xs">
              <thead className="sticky top-0 z-10 bg-[color:var(--surface-strong)]">
                <tr className="border-b border-[color:var(--border)] text-left text-[color:var(--text-muted)]">
                  {tableColumns.map((col) => (
                    <th key={col.key} className="px-2 py-2 font-semibold uppercase tracking-[0.06em]">
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredTableRows.map((row) => (
                  <tr key={`${row.id_os}-${row.data_fechamento}`} className="border-b border-[color:var(--border)]/40 hover:bg-[color:var(--surface-muted)]/40">
                    <td className="px-2 py-2">{row.id_os}</td>
                    <td className="px-2 py-2">{row.assunto}</td>
                    <td className="px-2 py-2">{row.referencia}</td>
                    <td className="px-2 py-2">{row.cidade}</td>
                    <td className="px-2 py-2">{row.status}</td>
                    <td className="px-2 py-2">{row.evento_periodo}</td>
                    <td className="px-2 py-2">{row.data_abertura}</td>
                    <td className="px-2 py-2">{row.data_hora_execucao}</td>
                    <td className="px-2 py-2">{row.data_fechamento}</td>
                    <td className="px-2 py-2">{formatInfraDurationHours(row.horas)}</td>
                    <td className="px-2 py-2">{currencyFormatter.format(row.valor)}</td>
                    <td className="px-2 py-2">{row.rompimento}</td>
                  </tr>
                ))}
                {filteredTableRows.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="px-2 py-6 text-center text-sm text-[color:var(--text-muted)]">
                      Nenhum registro encontrado para a busca informada.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdemServicoDashboard;
